import React, {
   CSSProperties,
   FocusEvent,
   forwardRef,
   ReactElement,
   ReactNode,
   Ref,
   useState
} from 'react';
import { Input } from '../base-input/Input';
import { Icons } from '@unifocus/react-icons';

/**
 * {@docCategory PasswordInput}
 */
export interface PasswordInputProps {
   /**
    * The name of the input item.
    */
   name?: string;

   /**
    * The value.
    */
   value?: string;

   /**
    * The placeholder text for the input item.
    */
   placeholder?: string;

   /**
    * Sets the input item to read-only.
    * @defaultvalue false
    */
   readOnly?: boolean;

   /**
    * Set the input item to disabled.
    * @defaultvalue false
    */
   disabled?: boolean;

   /**
    * Sets the width.
    */
   width?: string | number;

   /**
    * Sets the min width.
    */
   minWidth?: string | number;

   /**
    * Sets the max width.
    */
   maxWidth?: string | number;

   /**
    * Determines if a border should be shown around the component.
    * @defaultvalue true
    */
   border?: boolean;

   /**
    * Determines if the component should have internal padding.
    * @defaultvalue true
    */
   padding?: boolean;

   /**
    * Sets the maximum number of characters that can be entered.
    */
   maxLength?: number;

   /**
    * Enable the clear feature.
    * @defaultvalue false
    */
   allowClear?: boolean;

   /**
    * The prefix element.
    */
   prefix?: ReactNode;

   /**
    * The label text displayed before (on the left side of) the input field.
    */
   addonBefore?: ReactNode;

   /**
    * The label text displayed after (on the right side of) the input field.
    */
   addonAfter?: ReactNode;

   /**
    * Sets the popupTrigger.
    */
   popupTrigger?: ReactElement;

   /**
    * Set this to focus on first render
    */
   autoFocus?: boolean;

   /**
    * Whether or not to trigger change events on blur
    * @defaultvalue false
    */
   triggerOnBlur?: boolean;

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback called when the value is changed.
    * @param value - The value
    */
   onChange?: (value?: string) => void;

   /**
    * Callback called when the component loses focus.
    * @param event - The focus event object
    */
   onBlur?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Callback called when the component gains focus.
    * @param event - The focus event object
    */
   onFocus?: (event: FocusEvent<HTMLInputElement>) => void;

   /**
    * Function called when the user presses the enter key.
    */
   onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
   (props: PasswordInputProps, ref: Ref<HTMLInputElement>) => {
      const [maskPassword, setMaskPassword] = useState(true);
      const eyeIcon = <div
         className={'uf-password-input-eye'}
         onClick={() => {
            setMaskPassword(!maskPassword);
         }}
         onMouseDown={(event) => {
            event.preventDefault();
         }}
         onMouseUp={(event) => {
            event.preventDefault();
         }}

      >
         {maskPassword ? <Icons.VisibilityOff/> : <Icons.VisibilityOn/>}
      </div>;

      return (
         <Input
            type={maskPassword ? 'password' : 'text'}
            ref={ref}
            name={props.name}
            value={props.value}
            placeholder={props.placeholder}
            readOnly={props.readOnly}
            disabled={props.disabled}
            width={props.width}
            minWidth={props.minWidth}
            maxWidth={props.maxWidth}
            border={props.border}
            padding={props.padding}
            maxLength={props.maxLength}
            allowClear={props.allowClear}
            prefix={props.prefix}
            suffix={eyeIcon}
            addonBefore={props.addonBefore}
            addonAfter={props.addonAfter}
            className={`uf-password-input ${props.className || ''}`}
            style={props.style}
            autoFocus={props.autoFocus}
            onChange={props.onChange}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onPressEnter={props.onPressEnter}
            triggerOnBlur={props.triggerOnBlur}
         />
      );
   });
